import { Injectable } from '@angular/core';
import {
    Observable,
    BehaviorSubject,
    pipe,
    forkJoin,
    of,
    combineLatest,
    zip,
    from
} from 'rxjs';
import {
    map,
    finalize,
    tap,
    first,
    filter,
    catchError,
    delay,
    mergeMap,
    take,
    switchMap,
    toArray
} from 'rxjs/operators';
import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders
} from '@angular/common/http';
import { LgvModel } from '../../data-models/longevity.model';
import { environment as env } from '../../../environments/environment';
import { LgvAuthMgrService } from './lgv-auth-mgr.service';
import { LgvTSHelper as HLPR, LgvTSHelper } from '../utility/ts-helper';
import { BlendModel } from '../../data-models/blend.model';
import { Router } from '@angular/router';
import { LgvMockDataService } from './lgv-mock-data.service';
import { select } from '@ngrx/store';
import { LgvPricingModel } from '../../features/pricing/models/pricing.model';
import { LgvLoanApp } from '../../data-models/lgv-loan-application.model';
import { LgvAnalyticsService } from './lgv-analytics.service';

@Injectable({
    providedIn: 'root'
})
export class LgvClientService {
    AppInstanceID: string;

    _ClientID: string = 'Loading';
    ClientID$ = new BehaviorSubject(this._ClientID);

    // SnapShot$ is only updated when we have enough info to populate the dashboard.  This one is pouplated regardless of missing info.
    // ***Needed for Missing Loan Page***
    _PartialSnapShot: LgvModel.ClientSnapshot = null;
    PartialSnapShot$ = new BehaviorSubject(this._PartialSnapShot);

    _SnapShot: LgvModel.ClientSnapshot = null;
    SnapShot$ = new BehaviorSubject(this._SnapShot);

    _isAnonymousClient = false;
    isAnonymousClient$ = new BehaviorSubject(this._isAnonymousClient);

    _ClientProperties: LgvModel.Property[] = null;
    ClientProperties$ = new BehaviorSubject(this._ClientProperties);

    numPricingScenarioOpportunitySearches = 0;

    _pricingScenarios: LgvPricingModel.PricingScenario[] = [];
    pricingScenarios$ = new BehaviorSubject(this._pricingScenarios);

    _pricingScenariosState: string = 'Loading';
    pricingScenariosState$ = new BehaviorSubject(this._pricingScenariosState);
    get isPricingScenariosLoading$(): Observable<boolean> {
        return this.pricingScenariosState$.pipe(map(x => x === 'Loading'));
    }
    get isPricingScenariosError$(): Observable<boolean> {
        return this.pricingScenariosState$.pipe(map(x => x === 'Error'));
    }
    get isPricingScenariosReady$(): Observable<boolean> {
        return this.pricingScenariosState$.pipe(map(x => x === 'Ready'));
    }

    isPropertyDeletedLoading$ = new BehaviorSubject(false);

    _SelectedProperty: LgvModel.Property = null;
    SelectedProperty$ = new BehaviorSubject(this._SelectedProperty);
    _SelectedClientLoan: LgvModel.Loan = null;
    SelectedClientLoan$ = new BehaviorSubject(this._SelectedClientLoan);
    _SelectedClientLoanScenario: LgvModel.LoanScenario = null;
    SelectedClientLoanScenario$ = new BehaviorSubject(
        this._SelectedClientLoanScenario
    );
    _HaveVaultDocumentsPreCheck: boolean = false;
    HaveVaultDocumentsPreCheck$ = new BehaviorSubject(
        this._HaveVaultDocumentsPreCheck
    );

    _VaultDocuments: LgvModel.HomeVaultDocument[] = null;
    VaultDocuments$ = new BehaviorSubject(this._VaultDocuments);

    _HaveIncompleteLoanInformation: boolean = false;
    HaveIncompleteLoanInformation$ = new BehaviorSubject(
        this._HaveIncompleteLoanInformation
    );

    _Equity: LgvModel.Equity = null;
    Equity$ = new BehaviorSubject(this._Equity);

    StickyPropertyID: string = null;
    StickyLoanID: string = null;

    // Blend Details
    _BlendData: BlendModel.BlendResponse = null;
    BlendData$ = new BehaviorSubject(this._BlendData);

    // prevent multiple PR checkups from being performed from page navigation or otherwise
    PRCheckupEvaluatedThisSession: boolean = false;

    impersonatedClientKey = 'Lgv_ImpersonatedClientID';
    get SavedImpersonatedClientID(): string {
        let rtnVal = localStorage.getItem(this.impersonatedClientKey);
        return rtnVal && this.currentUserMatchesImpersonatedOwnerEmail
            ? rtnVal
            : null;
    }
    set SavedImpersonatedClientID(x: string) {
        localStorage.setItem(this.impersonatedClientKey, x);
    }
    impersonatedOwnerEmailKey = 'Lgv_ImpersonatedOwnerEmail';
    get SavedImpersonatedOwnerEmail(): string {
        return localStorage.getItem(this.impersonatedOwnerEmailKey);
    }
    set SavedImpersonatedOwnerEmail(x: string) {
        localStorage.setItem(this.impersonatedOwnerEmailKey, x);
    }
    get currentUserMatchesImpersonatedOwnerEmail(): boolean {
        return (
            this.authMgr.Auth0UserInfo$.value?.email ===
            this.SavedImpersonatedOwnerEmail
        );
    }

    _allOpportunities: LgvModel.Opportunity[] = [];
    allOpportunities$ = new BehaviorSubject(this._allOpportunities);

    isBetaClient$ = new BehaviorSubject(null);

    get hasPurchaseOpportunities$(): Observable<boolean> {
        return this.allOpportunities$.pipe(
            filter(f => f !== null),
            map(
                oppArray =>
                    oppArray?.filter(
                        o =>
                            o.propertyAssociationType === 'None' ||
                            o.propertyAssociationType === 'Prospect'
                    ).length > 0
            )
        );
    }
    get hasRefiOpportunities$(): Observable<boolean> {
        return this.allOpportunities$.pipe(
            filter(f => f !== null),
            map(
                oppArray =>
                    oppArray?.filter(o => o.propertyAssociationType === 'Owned')
                        .length > 0
            )
        );
    }
    get hasRefiAndPurchaseOpportunities$(): Observable<boolean> {
        return this.hasPurchaseOpportunities$.pipe(
            mergeMap(havePurchaseOpps =>
                this.hasRefiOpportunities$.pipe(
                    filter(f => f !== null),
                    map(haveRefiOpps => havePurchaseOpps && haveRefiOpps)
                )
            )
        );
    }

    // changing this logic from looking at property association type == "Owned" to opportunityInfo.loanPurpose == "Purchase"
    get refiOpportunities$(): Observable<LgvModel.Opportunity[]> {
        return this.allOpportunities$.pipe(
            filter(f => f !== null),
            map(oppArray =>
                oppArray?.filter(
                    o => o.opportunityInfo?.loanPurpose !== 'Purchase'
                )
            )
        );
    }
    get purchaseOpportunities$(): Observable<LgvModel.Opportunity[]> {
        return this.allOpportunities$.pipe(
            filter(f => f !== null),
            map(oppArray =>
                oppArray?.filter(
                    o => o.opportunityInfo?.loanPurpose === 'Purchase'
                )
            )
        );
    }

    get opportunitiesForSelectedProperty$(): Observable<
        LgvModel.Opportunity[]
    > {
        return this.scenariosForSelectedProperty$.pipe(
            filter(f => f !== null),
            mergeMap(scenarios =>
                this.allOpportunities$.pipe(
                    filter(f => f !== null),
                    map(opportunities => {
                        let psIDs: string[] = scenarios.map(x => x.id);
                        return opportunities?.filter(f =>
                            psIDs.includes(f.opportunityInfo?.pricingScenarioId)
                        );
                    })
                )
            )
        );
    }

    get hasOpportunitiesForSelectedProperty$(): Observable<boolean> {
        return this.opportunitiesForSelectedProperty$.pipe(
            first(f => f !== null),
            map(arr => arr && arr.length > 0)
        );
    }
    get hasOpportunitiesForSelectedPropertyWithSavings$(): Observable<boolean> {
        return this.opportunitiesForSelectedProperty$.pipe(
            first(f => f !== null),
            map(
                arr =>
                    arr &&
                    arr.length > 0 &&
                    arr.find(x => x.opportunityInfo.paymentSavings > 0) !==
                        undefined
            )
        );
    }

    opportunitiesForThisProperty(propertyId: string): LgvModel.Opportunity[] {
        let scenarios = this.pricingScenarios$.value;
        let opportunities = this.allOpportunities$.value;
        if (!scenarios || !opportunities) {
            return [];
        }

        let psIDs: string[] = scenarios
            .filter(f => f.propertyId === propertyId)
            .map(x => x.id);
        return opportunities?.filter(f =>
            psIDs.includes(f.opportunityInfo?.pricingScenarioId)
        );
    }

    opportunitiesForThisScenario(scenarioId: string): LgvModel.Opportunity[] {
        let opportunities = this.allOpportunities$.value;
        if (!opportunities) {
            return [];
        }
        return opportunities?.filter(
            f => f.opportunityInfo.pricingScenarioId === scenarioId
        );
    }

    findCachedScenario(scenarioID) {
        return this.pricingScenarios$.value?.find(f => f.id === scenarioID);
    }

    isRefiScenario(scenario: LgvPricingModel.PricingScenario) {
        return scenario?.propertyAssociationType === 'Owned';
    }

    propertyForScenario(scenarioId: string) {
        let scenarios = this.pricingScenarios$.value;
        let properties = this.ClientProperties$.value;
        if (!scenarios || !properties) {
            return null;
        }

        let scenario = scenarios.find(x => x.id == scenarioId);
        return properties.find(x => x.propertyInfo.id === scenario?.propertyId);
    }

    get scenariosForSelectedProperty$(): Observable<
        LgvPricingModel.PricingScenario[]
    > {
        return this.SelectedProperty$.pipe(
            mergeMap(prop =>
                this.pricingScenarios$.pipe(
                    filter(f => f !== null),
                    map(scenarios => {
                        return scenarios.filter(
                            s => s?.propertyId === prop?.propertyInfo?.id
                        );
                    })
                )
            )
        );
    }

    scenariosForThisProperty(
        propertyId: string
    ): LgvPricingModel.PricingScenario[] {
        let scenarios = this.pricingScenarios$.value;
        return scenarios.filter(f => f.propertyId === propertyId);
    }

    get propertylessScenarios$(): Observable<
        LgvPricingModel.PricingScenario[]
    > {
        return this.pricingScenarios$.pipe(
            filter(f => f !== null),
            map(scenarios =>
                scenarios?.filter(
                    ps => !ps?.propertyId && !ps?.loanApplicationId
                )
            )
        );
    }

    get loanAppScenarios$(): Observable<LgvPricingModel.PricingScenario[]> {
        return this.pricingScenarios$.pipe(
            filter(f => f !== null),
            map(scenarios => scenarios?.filter(ps => !!ps?.loanApplicationId))
        );
    }

    // return loans from all properties
    get allClientLoans$(): Observable<LgvModel.Loan[]> {
        return this.ClientProperties$.pipe(
            filter(f => f !== null),
            mergeMap(properties =>
                from(properties).pipe(
                    mergeMap(prop => prop.loans),
                    toArray()
                )
            )
        );
    }
    get allActiveLoans$(): Observable<LgvModel.Loan[]> {
        return this.allClientLoans$.pipe(
            map(loans => loans.filter(l => this.LoanIsActive(l)))
        );
    }
    get allInactiveLoans$(): Observable<LgvModel.Loan[]> {
        return this.allClientLoans$.pipe(
            map(loans => loans.filter(l => !this.LoanIsActive(l)))
        );
    }

    _OpportunitiesState: string = 'Ready';
    OpportunitiesState$ = new BehaviorSubject(this._OpportunitiesState);
    get IsOpportunitiesLoading$(): Observable<boolean> {
        return this.OpportunitiesState$.pipe(map(x => x === 'Loading'));
    }
    get IsOpportunitiesError$(): Observable<boolean> {
        return this.OpportunitiesState$.pipe(map(x => x === 'Error'));
    }
    get IsOpportunitiesReady$(): Observable<boolean> {
        return this.OpportunitiesState$.pipe(map(x => x === 'Ready'));
    }
    get isEquityManuallyAdjusted$(): Observable<boolean> {
        return this.SelectedProperty$.pipe(
            map(sp => sp?.clientPropertyAdjustments?.length > 0)
        );
    }

    get clientPersonas$(): Observable<string[]> {
        return this.ClientProperties$.pipe(
            filter(f => f !== null),
            map(arr => {
                if (arr.length == 0) {
                    return ['Propertyless'];
                } else {
                    let rtnVal = [];

                    if (
                        arr.filter(p => p.associationType === 'Prospect')
                            .length > 0
                    ) {
                        rtnVal.push('Prospect');
                    }
                    if (
                        arr.filter(p => p.associationType === 'Owned').length >
                        0
                    ) {
                        rtnVal.push('Owned');
                    }
                    if (
                        arr.filter(
                            p => p.associationType === 'Previously Owned'
                        ).length > 0
                    ) {
                        rtnVal.push('Previously Owned');
                    }
                    return rtnVal;
                }
            })
        );
    }

    get defaultClientHomePage$(): Observable<string> {
        return of('/client/client-overview');
    }

    // get defaultClientHomePage$(): Observable<string> {
    //     return this.SnapshotState$.pipe(filter(f => f !== '' && f !== 'Loading'),
    //         mergeMap(() => {
    //             return this.isPropertyless$.pipe(filter(f => f !== null), map(x => {
    //                 if (x == true) {
    //                     return '/client/client-overview';
    //                 }
    //                 else {
    //                     switch (this.SelectedProperty$.value?.associationType) {
    //                         case 'Prospect': return '/client/property-overview';
    //                         case 'Owned': return '/client/my-home';
    //                         case 'Previously Owned': return '/client/client-overview';
    //                         default: return '/client/client-overview';
    //                     }
    //                 }
    //             }
    //             ))

    //         }))
    // }

    get ownedProperties$(): Observable<LgvModel.Property[]> {
        return this.ClientProperties$.pipe(
            filter(f => f !== null),
            map(arr => arr.filter(p => p.associationType === 'Owned'))
        );
    }
    get prospectProperties$(): Observable<LgvModel.Property[]> {
        return this.ClientProperties$.pipe(
            filter(f => f !== null),
            map(arr => arr.filter(p => p.associationType === 'Prospect'))
        );
    }
    get previouslyOwnedProperties$(): Observable<LgvModel.Property[]> {
        return this.ClientProperties$.pipe(
            filter(f => f !== null),
            map(arr =>
                arr.filter(p => p.associationType === 'Previously Owned')
            )
        );
    }
    get ownedAndProspectProperties$(): Observable<LgvModel.Property[]> {
        return this.ClientProperties$.pipe(
            filter(f => f !== null),
            map(arr =>
                arr.filter(
                    p =>
                        p.associationType === 'Owned' ||
                        p.associationType === 'Prospect'
                )
            )
        );
    }
    get selectedPropertyIsOwned$(): Observable<boolean> {
        return this.SelectedProperty$.pipe(
            filter(f => f !== null),
            map(p => p.associationType === 'Owned')
        );
    }
    get selectedPropertyIsProspect$(): Observable<boolean> {
        return this.SelectedProperty$.pipe(
            filter(f => f !== null),
            map(p => p.associationType === 'Prospect')
        );
    }
    get hasOwnedProperties$(): Observable<boolean> {
        return this.ownedProperties$.pipe(
            filter(f => f !== null),
            map(arr => arr.length > 0)
        );
    }
    get hasProspectProperties$(): Observable<boolean> {
        return this.prospectProperties$.pipe(
            filter(f => f !== null),
            map(arr => arr.length > 0)
        );
    }
    get isOwner$(): Observable<boolean> {
        return this.hasOwnedProperties$;
    }
    get isBuyer$(): Observable<boolean> {
        return this.hasProspectProperties$;
    }
    get isPropertyless$(): Observable<boolean> {
        return this.clientPersonas$.pipe(
            filter(f => f !== null),
            map(arr => arr.filter(p => p === 'Propertyless').length > 0)
        );
    }

    public navigateToHomePage(clientId: string = null) {
        this.defaultClientHomePage$
            .pipe(first(f => f !== null))
            .subscribe(path => {
                if (clientId && clientId.length > 0) {
                    this.router.navigate([path, clientId]);
                } else {
                    this.router.navigate([path]);
                }
            });
    }

    get primaryProperty$(): Observable<LgvModel.Property> {
        return this.ClientProperties$.pipe(
            first(f => f !== null),
            map(m1 => m1.filter(f => f.isPrimaryProperty === true)),
            map(m => (m.length > 0 ? m[0] : null))
        );
    }

    _SnapshotState: string = '';
    SnapshotState$ = new BehaviorSubject(this._SnapshotState);
    get IsSSLoading$(): Observable<boolean> {
        return this.SnapshotState$.pipe(map(x => x === 'Loading'));
    }
    get IsSSError$(): Observable<boolean> {
        return this.SnapshotState$.pipe(map(x => x === 'Error'));
    }
    get IsSSReady$(): Observable<boolean> {
        return this.SnapshotState$.pipe(map(x => x === 'Ready'));
    }
    get HaveIncompleteSnapShot$(): Observable<boolean> {
        return this.SnapshotState$.pipe(
            map(x => x === 'Error' && this.PartialSnapShot$.value !== null)
        );
    }
    get IsSignificantEquityGained$(): Observable<boolean> {
        return this.Equity$.pipe(
            map(x => x != null && x.isSignificantEquity === 1)
        );
    }
    get clientHasLoanApp$(): Observable<boolean> {
        return this.SnapShot$.pipe(
            first(f => !!f),
            map(ss => ss.hasLoanApplication)
        );
    }

    get IsEPO$(): Observable<boolean> {
        if (this.SelectedClientLoan$.value === null) {
            return of(false);
        }
        return this.SelectedClientLoan$.pipe(
            filter(f => f !== null),
            map(l => {
                return (
                    l.loanAnalysis && l.loanAnalysis.notEligibleForOpportunity
                );
            })
        );
    }

    get ClientInfo$(): Observable<LgvModel.ClientInfo> {
        return this.SnapShot$.pipe(
            first(f => f !== null && f.clientInfo !== null),
            map(ss => ss.clientInfo)
        );
    }

    get ClientHasRegisteredWithAuth0$(): Observable<boolean> {
        return this.ClientInfo$.pipe(
            map(info => info.identityServiceId !== null)
        );
    }

    get calcDTI$(): Observable<string> {
        return this.SnapShot$.pipe(
            first(f => f !== null && f.clientInfo !== null),
            map(ss => ss.clientInfo),
            map(ci =>
                ci.totalDebtPayments &&
                ci.totalDebtPayments > 0 &&
                ci.totalIncome &&
                ci.totalIncome > 0
                    ? ((ci.totalDebtPayments / ci.totalIncome) * 100).toFixed(0)
                    : null
            )
        );
    }

    get IsOwnerReadyForPricing$(): Observable<boolean> {
        return this.SnapShot$.pipe(
            map(ss => {
                return ss?.clientInfo?.owner?.isReadyForPricing;
            })
        );
    }

    ClientHasRequiredInsuranceFields$ = this.SnapShot$.pipe(
        filter(f => f !== null),
        //tap(t => console.log(`ClientHasRequiredInsuranceFields$: ${t.clientInfo.firstName} - ${t.clientInfo.lastName} - ${t.clientInfo.birthDate}`)),
        map(ss => {
            return (
                !HLPR.stringIsNullOrEmpty(ss.clientInfo.firstName) &&
                !HLPR.stringIsNullOrEmpty(ss.clientInfo.lastName) &&
                //proerty.address?
                // property.city?
                // property.state?
                // property.zip?
                ss.clientInfo.birthDate !== null
            );
        })
    );

    constructor(
        private http: HttpClient,
        private router: Router,
        private authMgr: LgvAuthMgrService,
        private mockDataSvc: LgvMockDataService,
        private analytics: LgvAnalyticsService
    ) {
        this.AppInstanceID = LgvTSHelper.generateNewGuid();

        this.authMgr.IsAuthenticated$.subscribe(x => {
            if (!x) {
                return;
            }

            if (this.authMgr.IsClient) {
                // If user is both client and secureClient, two auth events will fire.  Only load snapshot for IsClient
                if (!this.authMgr.IsSecureClient) {
                    this.LoadSnapshotForClient();

                    this.loadScenarios();
                    this.loadOpportunities();

                    this.PostDebugEvent(
                        'User Authenticated'
                    ).subscribe(() => {});
                }
            } else if (this.SavedImpersonatedClientID) {
                // need this to make agent / client impersonation work when the clientID is not longer in the URL
                this.LoadSnapshotForNonClient(this.SavedImpersonatedClientID);
            }
        });
    }

    public LoadSnapshotForClient(forceRefresh = false) {
        //console.log('%%%%%%%%%% LoadShapshotForClient() %%%%%%%%%%');
        this.SnapshotState$.next('Loading');
        this.pricingScenariosState$.next('Loading');

        if (forceRefresh) {
            this.ClientProperties$.next(null);
            this.SelectedProperty$.next(null);
            this.SnapShot$.next(null);
            this.pricingScenarios$.next(null);
            this.SetSelectedClientPropertyAndLoan(null, null);
            this.setSelectedClientPropertyAndLoanScenario(null, null);
            this.loadScenarios();
            this.loadOpportunities();
        }

        // *** MOCK CLIENT DATA ***
        if (env.envName.toUpperCase() === 'DEV' && env.useMockData) {
            setTimeout(() => {
                this.mockDataSvc
                    .mockClientSnapshot()
                    .pipe(first())
                    .subscribe(ss => {
                        console.log(
                            '=============================================='
                        );
                        console.log(
                            'x=x=x=x=x=x=x= MOCK CLIENT DATA x=x=x=x=x=x=x='
                        );
                        console.log(
                            '=============================================='
                        );

                        const propertyLessClientMode = true;
                        if (propertyLessClientMode) {
                            ss.properties = [];
                        }
                        this.InitFromSnapshot(ss);
                    });
            }, 500);
        } else {
            // *** STANDARD CLIENT DATA ***
            this.http
                .get<LgvModel.ClientSnapshot>(
                    `${env.LgvApiBaseUrl}/user/client/snapshot`
                )
                .pipe(
                    first(),
                    catchError(error => {
                        this.SnapshotState$.next('Error');
                        return this.SnapShot$;
                    })
                )
                .subscribe(ss => {
                    //console.log('%%%%%%%%%% Client Snapshot Loaded %%%%%%%%%%');
                    this.InitFromSnapshot(ss);
                });
        }
    }

    public LoadOpportunitiesForPricingScenario(
        ps: LgvPricingModel.PricingScenario,
        forceRefresh = false
    ) {
        this.OpportunitiesState$.next('Loading');
        this.numPricingScenarioOpportunitySearches++;
        this.getClientOpportunities(
            ps.clientId,
            null,
            null,
            ps.id,
            forceRefresh
        )
            .pipe(
                filter(f => !!f),
                take(1)
            )
            .subscribe(
                opportunities => {
                    for (let o of opportunities) {
                        this.setPropertyStatusForOpportunity(o);
                    }

                    let tmpOpportunities = this.allOpportunities$.value;
                    if (tmpOpportunities && tmpOpportunities.length > 0) {
                        // clear existing opportunities for this PS
                        tmpOpportunities = tmpOpportunities.filter(
                            x => x.opportunityInfo.pricingScenarioId !== ps.id
                        );
                    } else {
                        tmpOpportunities = [];
                    }

                    this.allOpportunities$.next(
                        tmpOpportunities.concat(opportunities)
                    );

                    if (--this.numPricingScenarioOpportunitySearches === 0) {
                        this.OpportunitiesState$.next('Ready');
                    }
                },
                err => {
                    console.log('Error trying to fetch opportunities');
                    console.log(err);
                    this.OpportunitiesState$.next('Error');
                }
            );
    }

    public LoadSnapshotForNonClient(
        id: string,
        updateLoanInfo: boolean = false,
        forceRefresh: boolean = false
    ) {
        //Converting this method to be used for "Client" as well as the previous usage (for non-clients)

        //console.log('==============  LoadSnapshotForNonClient =====================');

        if (
            this.SnapShot$.value &&
            this.SnapShot$.value.clientInfo &&
            this.SnapShot$.value.clientInfo.id === id &&
            updateLoanInfo === false &&
            forceRefresh === false
        ) {
            //console.log('*** Keeping existing SnapShot ***');
            return;
        } else {
            //console.log('*** NEW SnapShot ***');
            this.ClientProperties$.next(null);
            this.SelectedProperty$.next(null);
            this.pricingScenarios$.next(null);
            this.SnapShot$.next(null);
            this.SetSelectedClientPropertyAndLoan(null, null);
            this.setSelectedClientPropertyAndLoanScenario(null, null);
            this.allOpportunities$.next(null);
            this.SavedImpersonatedClientID = id;
            this.SavedImpersonatedOwnerEmail = this.authMgr.Auth0UserInfo$.value?.email;
        }

        //let objUrl = this.authMgr.IsAdmin ? 'clients' : 'user/agent/clients';

        this.SnapshotState$.next('Loading');
        this.pricingScenariosState$.next('Loading');

        // *** MOCK CLIENT DATA ***
        if (env.envName.toUpperCase() === 'DEV' && env.useMockData) {
            setTimeout(() => {
                this.mockDataSvc
                    .mockClientSnapshot()
                    .pipe(first())
                    .subscribe(ss => {
                        console.log(
                            '=============================================='
                        );
                        console.log(
                            'x=x=x=x=x=x=x= MOCK CLIENT DATA x=x=x=x=x=x=x='
                        );
                        console.log(
                            '=============================================='
                        );

                        this.InitFromSnapshot(ss);
                        this.loadScenarios(ss?.clientInfo?.id);
                    });
            }, 500);
        } else {
            this.FetchSnapshot(id).subscribe(
                ss => {
                    //console.log('****** LoadSnapshotForNONClient.InitFromSnapshot() ******')
                    this.InitFromSnapshot(ss);
                    this.loadScenarios(id);
                },
                err => this.SnapshotState$.next('Error')
            );

            this.loadOpportunities();
        }
    }

    public FetchSnapshot(id: string): Observable<LgvModel.ClientSnapshot> {
        return this.http
            .get<LgvModel.ClientSnapshot>(
                `${env.LgvApiBaseUrl}/clients/${id}/snapshot`
            )
            .pipe(first());
    }

    public FetchClientInfo(id: string): Observable<LgvModel.ClientInfo> {
        return this.http
            .get<LgvModel.ClientInfo>(`${env.LgvApiBaseUrl}/clients/${id}`)
            .pipe(first());
    }

    private InitFromSnapshot(ss: LgvModel.ClientSnapshot) {
        if (!ss) {
            console.log('NULL Snapshot - returning');
            return;
        }

        this.ResetInsuranceQuoteInfo();
        this.ClientID$.next(ss.clientInfo.id);
        this.isBetaClient$.next(!!ss?.clientInfo?.isBeta);

        // Always update this version, regardless of missing info
        this.PartialSnapShot$.next(ss);

        if (ss.properties && ss.properties.length > 0) {
            // filter out incomplete loans
            for (let p of ss.properties) {
                let numLoansBefore = p.loans.length;

                p.loans = p.loans.filter(f => this.IsCompleteLoan(f));

                if (numLoansBefore > p.loans.length) {
                    console.log(
                        `*** Filtered out ${numLoansBefore -
                            p.loans.length} incomplete loans ***`
                    );
                }
            }

            // sort by presence of loan or loanScenario, then by createdTimestamp
            let sortedProperties = ss.properties.sort((a, b) =>
                (a.loans && a.loans.length > 0) ||
                (a.loanScenarios &&
                    a.loanScenarios.length > 0 &&
                    a.propertyInfo.createdTimestamp >
                        b.propertyInfo.createdTimestamp)
                    ? -1
                    : 1
            );

            let property = sortedProperties[0];

            let stickyProperty: LgvModel.Property = null;

            if (this.StickyPropertyID) {
                stickyProperty = sortedProperties.find(
                    p => p.propertyInfo.id === this.StickyPropertyID
                );
                if (stickyProperty) {
                    property = stickyProperty;
                }
            }

            this.ClientProperties$.next(sortedProperties);
            this.SetSelectedProperty(property);

            // set loan
            if (
                property &&
                property.loans &&
                property.loans.length > 0 &&
                property.loans[0] &&
                property.loans[0].loanAnalysis
            ) {
                try {
                    let sortedLoans = property.loans.sort((a, b) =>
                        this.LoanCompareOrder(a, b)
                    );

                    let loanHistory = sortedLoans.filter(
                        f => !this.LoanIsActive(f)
                    );
                    let clientLoans = sortedLoans.filter(f =>
                        this.LoanIsActive(f)
                    );

                    // if we don't hanve any client loans (nothing active), then add an old loan so the UI has something to show
                    if (clientLoans.length === 0 && loanHistory.length > 0) {
                        clientLoans.push(loanHistory[0]);
                    }

                    let stickyLoan: LgvModel.Loan = null;

                    if (this.StickyLoanID) {
                        stickyLoan = clientLoans.find(
                            l => l.loanInfo.id === this.StickyLoanID
                        );
                    }

                    this.SetSelectedClientPropertyAndLoan(
                        property,
                        stickyLoan ? stickyLoan : sortedLoans[0]
                    );

                    // if we have a successful loan set up, check for documents
                    /* ********************** Holding off on this feature ********************** */
                    //this.CheckForDocuments();
                    /* ********************** Holding off on this feature ********************** */
                } catch (e) {
                    console.log('Error assigning Loans:');
                    console.log(e);
                    this.SnapshotState$.next('Error');
                    return;
                }
            }
            // set loanScenario
            if (
                property &&
                property.loanScenarios.length > 0 &&
                property.loanScenarios[0]
            ) {
                this.setSelectedClientPropertyAndLoanScenario(
                    property,
                    property.loanScenarios[0]
                );
            }
        } else {
            // Properties are no longer required!!!
            this.ClientProperties$.next([]);
        }

        this.SnapShot$.next(ss);
        this.SnapshotState$.next('Ready');
        this.CustomizeOpportunityDisplayOrderFromGoals();
    }

    private CustomizeOpportunityDisplayOrderFromGoals() {
        //let loan = this.SelectedClientLoan$.value;
        //if (!loan || !this.SnapShot$.value || !this.SnapShot$.value.goals || !this.Opportunities$.value || !(this.Opportunities$.value.length > 0)) {
        //    return;
        //}
        //let goals = this.SnapShot$.value.goals.filter(f => f.enabled);
        //if (goals.length === 0) {
        //    return;
        //}
        //let lowerPmt: boolean = goals.filter(g => g.description.indexOf('payment') > -1).length > 0;
        //let ownSooner: boolean = goals.filter(g => g.description.indexOf('sooner') > -1).length > 0;
        //if (lowerPmt) {
        //    return;  // Opportunitites are already sorted by payment
        //}
        //if (ownSooner) {
        //    // sort by loan term, then savings as tie breaker.
        //    this.Opportunities$.next(loan.opportunities.sort((a, b) =>
        //        a.opportunityInfo.loanTermMonths < b.opportunityInfo.loanTermMonths
        //            || a.opportunityInfo.paymentSavings > b.opportunityInfo.paymentSavings ? -1 : 1
        //    ));
        //}
    }

    public SetSelectedClientPropertyAndLoan(
        property: LgvModel.Property,
        loan: LgvModel.Loan
    ) {
        this.ResetInsuranceQuoteInfo();

        if (property) {
            this.StickyPropertyID = property.propertyInfo.id;

            if (!loan && property.loans && property.loans.length > 0) {
                loan = this.PickBestLoanForProperty(property);
            }
        }

        if (loan) {
            this.StickyLoanID = loan.loanInfo.id;
        }

        //this.Opportunities$.next([]);

        // clear the loan so subscribers to SelectedProperty$ don't have a loan till the new loan is set (next line)
        this.SelectedClientLoan$.next(null);

        //console.log('Setting SelectdedProperty$ - 2');
        this.SetSelectedProperty(property);
        this.SelectedClientLoan$.next(loan);

        if (loan) {
            this.FetchClientEquityGained(
                this.ClientID$.value,
                loan.loanInfo.id
            );
            //this.LoadOpportunitiesForLoanOrLoanScenario(this.ClientID$.value, loan.loanInfo.id, null);
        } else {
            this.Equity$.next(null);
        }
    }

    setSelectedClientPropertyAndLoanScenario(
        property: LgvModel.Property,
        loanScenario: LgvModel.LoanScenario
    ) {
        if (property) {
            this.StickyPropertyID = property.propertyInfo.id;
        }

        // clear the loan so subscribers to SelectedProperty$ don't have a loan till the new loan is set (next line)
        this.SelectedClientLoanScenario$.next(null);

        //console.log('Setting SelectdedProperty$ - 2');
        this.SetSelectedProperty(property);
        this.SelectedClientLoanScenario$.next(loanScenario);

        if (loanScenario) {
            //this.LoadOpportunitiesForLoanOrLoanScenario(this.ClientID$.value, null, loanScenario.id);
        } else {
            // reset opportunities value to empty array if no loanScenario
            //this.Opportunities$.next([]);
            this.Equity$.next(null);
        }
    }

    SetSelectedProperty(property: LgvModel.Property) {
        if (
            property &&
            this.SelectedProperty$.value &&
            this.SelectedProperty$.value.propertyInfo.id ===
                property.propertyInfo.id
        ) {
            //console.log('*x*x*x* Skipping new property assignment *x*x*x*');
            return;
        }

        //this.Opportunities$.next([]);
        this.SelectedClientLoan$.next(null);
        this.SelectedClientLoanScenario$.next(null);
        this.SelectedProperty$.next(property);
    }

    sendNewClientInvitationEmail(email: string): Observable<any> {
        const encodedEmail = encodeURIComponent(email);
        const bldUrl = `${env.LgvApiBaseUrl}/auth/client/request?email=${encodedEmail}`;

        const model = {};

        return this.http.post<any>(bldUrl, model);
    }

    public fetchMarketValueForSelectedProperty(
        propId: string
    ): Observable<LgvModel.MarketValueHistory[]> {
        if (!propId) {
            return of([]);
        }

        return this.http.get<LgvModel.MarketValueHistory[]>(
            `${env.LgvApiBaseUrl}/properties/${propId}/valuation/history`
        );
    }

    //#region Scenarios
    public fetchScenarios(
        clientID: string
    ): Observable<LgvPricingModel.PricingScenario[]> {
        return this.http.get<LgvPricingModel.PricingScenario[]>(
            `${env.LgvApiBaseUrl}/pricing/scenario/client/${clientID}`
        );
    }

    public loadOpportunities(forceRefresh = false) {
        let clientId = this.authMgr.IsClient
            ? this.authMgr.ClientInfo?.id
            : this.SavedImpersonatedClientID;
        if (!clientId) {
            return;
        }
        this.OpportunitiesState$.next('Loading');

        this.getClientOpportunities(clientId, null, null, null, forceRefresh)
            .pipe(first())
            .subscribe(
                opps => {
                    for (let o of opps) {
                        this.setPropertyStatusForOpportunity(o);
                    }
                    this.allOpportunities$.next(opps);
                    this.OpportunitiesState$.next('Ready');
                },
                err => {
                    console.log('Error trying to fetch opportunities');
                    console.log(err);
                    this.OpportunitiesState$.next('Error');
                }
            );
    }

    public loadScenarios(clientID?: string) {
        if (!!!clientID) {
            clientID = this.authMgr.IsClient
                ? this.authMgr.ClientInfo?.id
                : this.SavedImpersonatedClientID;
        }

        this.pricingScenariosState$.next('Loading');
        this.fetchScenarios(clientID)
            .pipe(
                filter(f => !!f),
                take(1),
                catchError(err => {
                    this.pricingScenariosState$.next('Error');
                    return of([] as LgvPricingModel.PricingScenario[]);
                })
            )
            .subscribe(
                scenariosResult => {
                    this.pricingScenarios$.next(scenariosResult);

                    for (let ps of scenariosResult) {
                        this.setPropertyStatusForScenario(ps);
                    }

                    this.pricingScenariosState$.next('Ready');
                },
                err => {
                    if (err instanceof HttpErrorResponse) {
                        const httpError: HttpErrorResponse = err as HttpErrorResponse;

                        if (httpError?.status === 404) {
                            this.pricingScenarios$.next([]);
                            this.pricingScenariosState$.next('Ready');
                        }
                    } else {
                        console.log('Error trying to fetch pricing scenarios');
                        console.log(err);
                    }
                }
            );
    }

    setPropertyStatusForScenario(ps: LgvPricingModel.PricingScenario) {
        let prop = this.propertyForScenario(ps.id);
        ps.propertyAssociationType = prop ? prop?.associationType : 'None';
    }

    setPropertyStatusForOpportunity(o: LgvModel.Opportunity) {
        let prop = this.propertyForScenario(
            o.opportunityInfo.pricingScenarioId
        );
        o.propertyAssociationType = prop ? prop?.associationType : 'None';
    }

    public updateScenario(scenarioId: string, scenarioBody) {
        return this.http.patch<LgvPricingModel.PricingScenario[]>(
            `${env.LgvApiBaseUrl}/pricing/scenario/${scenarioId}`,
            scenarioBody
        );
    }

    //#endregion Scenarios

    //#region SecureClient

    RegisterSecureClient() {
        let bldUrl = `${env.LgvApiBaseUrl}/user/client/secured-account/register`;

        let model = {};

        console.log('Registering SecureClient()');

        this.http
            .post<any>(bldUrl, model)
            .pipe(first())
            .subscribe(
                x => {
                    console.log('Result from Client Register');
                    console.log(x);
                },
                err => {
                    console.log('Error trying to register SecureClient');
                    console.log(err);
                }
            );
    }

    public CheckForDocuments() {
        // right now we only have endpoint for client role
        if (!this.authMgr.IsClient) {
            return;
        }

        let loanID = this.SelectedClientLoan$.value.loanInfo.id;

        this.http
            .get<any>(
                `${env.LgvApiBaseUrl}/user/client/loans/${loanID}/encompass/documents/check`
            )
            .pipe(first(f => f !== null))
            .subscribe(
                x => {
                    //console.log('Result from document check');
                    //console.log(x);

                    let hasDocs =
                        x && x.hasOwnProperty('hasDocuments') && x.hasDocuments;
                    this.HaveVaultDocumentsPreCheck$.next(hasDocs);
                },
                err => {
                    console.log('Error checking for documents');
                    console.log(err);
                }
            );
    }

    public FetchDocuments() {
        if (
            this.VaultDocuments$.value &&
            this.VaultDocuments$.value.length > 0
        ) {
            console.log('Already have documents... Skipping fetch');
            return;
        }

        console.log('Fetching Documents');

        let loanID = this.SelectedClientLoan$.value.loanInfo.id;
        this.http
            .get<any>(
                `${env.LgvApiBaseUrl}/user/client/loans/${loanID}/encompass/documents`
            )
            .pipe(first(f => f !== null))
            .subscribe(
                x => {
                    console.log('Result from document fetch');
                    console.log(x);

                    this.VaultDocuments$.next(x);
                },
                err => {
                    console.log('Error fetching documents');
                    console.log(err);
                }
            );
    }

    public editLoan(clientId, loanId, model) {
        const bldUrl = `${env.LgvApiBaseUrl}/clients/${clientId}/loans/${loanId}`;
        return this.http.patch<any>(bldUrl, model);
    }

    public getLoanTypes() {
        const url = `${env.LgvApiBaseUrl}/loans/types/loans`;
        return this.http.get<any>(url);
    }

    public getLoanPurpose() {
        const url = `${env.LgvApiBaseUrl}/loans/types/purpose`;
        return this.http.get<any>(url);
    }

    //#endregion SecureClient

    //#region Hippo Insurance

    _ShowHippoQuote: boolean = false;
    ShowHippoQuote$ = new BehaviorSubject(this._ShowHippoQuote);

    _HippoQuote: LgvModel.InsuranceQuote = null;
    HippoQuote$ = new BehaviorSubject(this._HippoQuote);

    _HippoQuoteState: string = 'Default';
    HippoQuoteState$ = new BehaviorSubject(this._HippoQuoteState);

    _HippoQuoteErrorObj: any = null;
    HippoQuoteErrorObj$ = new BehaviorSubject(this._HippoQuoteErrorObj);

    get IsHippoQuoteLoading$(): Observable<boolean> {
        return this.HippoQuoteState$.pipe(map(x => x === 'Loading'));
    }
    get IsHippoQuoteMissingInfo$(): Observable<boolean> {
        return this.HippoQuoteState$.pipe(map(x => x === 'MissingInfo'));
    }
    get IsHippoQuoteError$(): Observable<boolean> {
        return this.HippoQuoteState$.pipe(map(x => x === 'Error'));
    }
    get IsHippoQuoteReady$(): Observable<boolean> {
        return this.HippoQuoteState$.pipe(map(x => x === 'Ready'));
    }

    ResetInsuranceQuoteInfo() {
        this.HippoQuoteState$.next('Default');
        this.HippoQuote$.next(null);
    }

    OnShowHippoQuote() {
        this.ShowHippoQuote$.next(true);
        this.FetchHippoQuote();
    }

    FetchHippoQuote(birthdate: Date = null) {
        if (birthdate) {
            if (this.SnapShot$.value) {
                this.SnapShot$.value.clientInfo.birthDate = birthdate;
            }

            this.ResetInsuranceQuoteInfo();
        } else if (this.HippoQuoteState$.value !== 'Default') {
            //console.log('Returning - Not Default');
            return;
        }

        this.ClientHasRequiredInsuranceFields$.subscribe(hasReq => {
            //console.log('ClientHasRequiredInsuranceFields$.subscribe | ' + this.HippoQuoteState$ + ' | ' + (hasReq as boolean) );

            if (!hasReq) {
                this.HippoQuoteState$.next('MissingInfo');
                return;
            }

            this.HippoQuoteState$.next('Loading');

            this.SelectedClientLoan$.pipe(first(f => f !== null)).subscribe(
                clientLoan => {
                    let bldUrl = `${env.LgvApiBaseUrl}`;

                    if (this.authMgr.IsClient) {
                        bldUrl += `/user/client/loans/${clientLoan.loanInfo.id}/insurancequote`;
                    } else {
                        bldUrl += `/clients/${this.ClientID$.value}/loans/${clientLoan.loanInfo.id}/insurancequote`;
                    }

                    if (birthdate !== null) {
                        bldUrl +=
                            '?birthDate=' +
                            LgvTSHelper.jsDateToJsonDate(birthdate, true);
                    }

                    this.http
                        .get<LgvModel.InsuranceQuote>(bldUrl)
                        .pipe(first())
                        .subscribe(
                            iq => {
                                this.HippoQuote$.next(iq);

                                if (iq.success) {
                                    this.HippoQuoteState$.next('Ready');
                                } else {
                                    this.HippoQuoteState$.next('Error');
                                }
                            },
                            errObj => {
                                this.HippoQuoteState$.next('Error');
                                this.HippoQuoteErrorObj$.next(errObj);
                                console.log('Hippo Error obj:');
                                console.log(errObj);
                            }
                        );
                }
            );
        });
    }

    //#endregion

    UpdatePropertyLastPRCheckupTimeStamp(propertyId: string) {
        let model = {
            lastPublicRecordCheckupDate: LgvTSHelper.jsDateToJsonDate(
                new Date()
            )
        };

        let bldUrl = `${env.LgvApiBaseUrl}/properties/${propertyId}`;
        this.http
            .patch<any>(bldUrl, model)
            .pipe(first())
            .subscribe(
                x => {
                    console.log('LastChekcupDate updated.');
                },
                err => {
                    console.log(
                        'There was an error trying to update Public Records checkup timestamp.'
                    );
                    console.log(err);
                }
            );
    }

    /*
    public SilenceThisClient(clientID: string, bSilence: boolean): Observable<any> {
        let bldUrl = env.LgvApiBaseUrl;
        bldUrl += `/clients/${clientID}/silence?silenced=${bSilence}`;
        let model = {};
        return this.http.patch<any>(bldUrl, model).pipe(first());
    }
    */

    MakeDTResultTheActiveLoan(
        prRequestId: string,
        prResultId: string
    ): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/publicrecords/createloan`;
        let searchModel = {
            requestId: prRequestId,
            resultId: prResultId
        };

        return this.http.post<any>(bldUrl, searchModel).pipe(
            first(),
            tap(result => {
                if (
                    result &&
                    result.propertyId &&
                    result.loanIds &&
                    result.loanIds.length > 0
                ) {
                    let propertyId = result.propertyId;
                    let loanId = result.loanIds[0];
                    this.StickyLoanID = loanId;

                    if (propertyId && this.authMgr.IsClient) {
                        this.UpdatePropertyLastPRCheckupTimeStamp(propertyId);
                    }
                }
            })
        );
    }

    public SilenceThisClient(
        clientID: string,
        bSilence: boolean
    ): Observable<any> {
        let bldUrl = env.LgvApiBaseUrl;
        bldUrl += `/clients/${clientID}/silence?silenced=${bSilence}`;
        let model = {};
        return this.http.patch<any>(bldUrl, model).pipe(first());
    }

    public UpdateClientGoal(goalID: string, isChecked: boolean) {
        let clientID: string = this.ClientID$.value;
        let bldUrl = `${env.LgvApiBaseUrl}/user/client/goals/${goalID}?enable=${isChecked}`;
        let model = {};

        this.http
            .post<LgvModel.Goal>(bldUrl, model)
            .pipe(first())
            .subscribe(x => {
                //console.log('Result:');
                //console.log(x);
            });

        // Manually update our copy of the goals so we don't have to reload
        let ss = this.SnapShot$.value;
        let goals: LgvModel.Goal[] = ss.goals;
        let g = goals.find(x => x.id == goalID);
        g.enabled = isChecked;
        ss.goals = goals;
    }

    /*  *Reminder* These methods need to be called w/ a .subscribe() */
    PostPageClick(page: string): Observable<any> {
        return this.PostClientActivity(
            '6CB99231-D176-40CD-BC51-83843A1AB81B',
            page
        );
    }
    PostClientLogin(): Observable<any> {
        return this.PostClientActivity('BEB73295-A8CA-EA11-8B03-000D3A3280F4');
    }
    PostClientClickedGoal(): Observable<any> {
        return this.PostClientActivity('BBB73295-A8CA-EA11-8B03-000D3A3280F4');
    }
    PostDebugEvent(notes: string): Observable<any> {
        return this.PostClientActivity(
            '3A5D0BC5-2A70-4D01-9D4F-BC15AB334674',
            notes
        );
    }
    PostClientRequestedCMA(notes: string): Observable<any> {
        return this.PostClientActivity(
            'BCB73295-A8CA-EA11-8B03-000D3A3280F4',
            notes
        );
    }
    PostViewInsuranceQuote(notes: string): Observable<any> {
        return this.PostClientActivity(
            '996A7220-F87F-41AA-9D12-9B7ECF2A5EFC',
            notes
        );
    }
    PostClientCheckup_NoNewLoanDetected(notes: string): Observable<any> {
        return this.PostClientActivity(
            'C61D4C84-878B-EC11-8CBD-000C290D53A0',
            notes
        );
    }
    PostClientCheckup_ClientAcceptedNewLoan(notes: string): Observable<any> {
        return this.PostClientActivity(
            'C71D4C84-878B-EC11-8CBD-000C290D53A0',
            notes
        );
    }
    PostClientCheckup_NewLoanRejected(notes: string): Observable<any> {
        return this.PostClientActivity(
            'C81D4C84-878B-EC11-8CBD-000C290D53A0',
            notes
        );
    }
    PostRateSearch(notes: string): Observable<any> {
        return this.PostClientActivity(
            'DB9C542A-D29A-EE11-8925-0022480B9099',
            notes
        );
    }

    PostClientActivity(
        activityTypeID: string,
        notes: string = null
    ): Observable<any> {
        // ignore activity for non authenticated clients
        if (!this.authMgr.IsClient) {
            return of({});
        }

        let bldUrl = `${env.LgvApiBaseUrl}/user/client/activities`;
        let model = {
            appInstanceId: this.AppInstanceID,
            activityTypeId: activityTypeID,
            timestamp: LgvTSHelper.utcDateAsJsonDate(),
            notes: notes
        };

        return this.http.post<LgvModel.Activity>(bldUrl, model).pipe(first());
    }

    /*  *Reminder* These methods need to be called w/ a .subscribe() */

    PostRequestCMAMessage(msg: string): Observable<any> {
        return this.PostClientMessage(
            '3B0DEEA6-5A32-41EC-BBCC-D56E56AB1C3C',
            msg
        );
    }
    PostCashOutMessage(msg: string): Observable<any> {
        return this.PostClientMessage(
            '3EE275B9-E38E-4197-B408-78EE71D2CE49',
            msg
        );
    }
    PostQuestionsMessage(msg: string): Observable<any> {
        return this.PostClientMessage(
            '44C30C75-B927-428D-AB75-9C8B69313769',
            msg
        );
    }
    PostRefinanceMessage(msg: string): Observable<any> {
        return this.PostClientMessage(
            '99DECF86-A47F-4153-A3F2-C8D1AE2855B7',
            msg
        );
    }

    static MessageTypeToID(msgType: string): string {
        switch (msgType) {
            case 'Cash Out':
                return '3EE275B9-E38E-4197-B408-78EE71D2CE49';
            case 'RequestInfoOnSellYourHome':
                return '3EE275B9-E38E-4197-B408-78EE71D2CE49';
            case 'Questions Widget':
                return '44C30C75-B927-428D-AB75-9C8B69313769';
            case 'Refinancing':
                return '99DECF86-A47F-4153-A3F2-C8D1AE2855B7';
            case 'CMA Request':
                return '3B0DEEA6-5A32-41EC-BBCC-D56E56AB1C3C';
            case 'Equity Gained':
                return '0CA675EA-8E40-4EEA-9A20-C357C46C1C91';
            case 'General':
                return '250B07A1-8F6B-47F3-9E13-F8E2BE418009';
            // Default to General
            default:
                return '250B07A1-8F6B-47F3-9E13-F8E2BE418009';
        }
    }

    PostClientMessageForType(
        msgType: string,
        msg: string = null
    ): Observable<any> {
        return this.PostClientMessage(
            LgvClientService.MessageTypeToID(msgType),
            msg
        );
    }

    PostClientMessage(
        clientMessageTypeID: string,
        msg: string = null
    ): Observable<any> {
        if (!this.authMgr.IsClient) {
            return of({});
        }

        let bldUrl = `${env.LgvApiBaseUrl}/user/client/message`;
        let model = {
            messageTypeId: clientMessageTypeID,
            message: msg
        };

        return this.http.post<LgvModel.Message>(bldUrl, model).pipe(first());
    }

    public clientSearch(
        searchText: string,
        companyId: string,
        organizationID: string,
        agentId: string
    ): Observable<any> {
        let company = companyId != '' ? `&companyId=${companyId}` : '';
        let organization = !organizationID
            ? ''
            : `&organizationId=${organizationID}`;
        let agent = agentId != '' ? `&agentId=${agentId}` : '';
        let ShowSilenced: boolean = false;
        let queryParam = `${company}${organization}${agent}`;
        let bldUrl = `${env.LgvApiBaseUrl}/clients?${queryParam}&silenced=${ShowSilenced}&txtSearch=${searchText}`;
        return this.http.get<any>(bldUrl).pipe(first());
    }

    public getClientCount() {
        let bldUrl = `${env.LgvApiBaseUrl}/clients/count`;
        return this.http.get<any>(bldUrl).pipe(first());
    }

    public SearchWebServiceClient(
        searchText: string,
        companyId: string,
        organizationID: string,
        agentId: string,
        filterOnClientsWithLoans: boolean
    ): Observable<any> {
        let company = companyId != '' ? `&companyId=${companyId}` : '';
        let organization = !organizationID
            ? ''
            : `&organizationId=${organizationID}`;
        let agent = agentId != '' ? `&agentId=${agentId}` : '';
        let ShowSilenced: boolean = false;
        let queryParam = `${company}${organization}${agent}`;
        let bldUrl = `${env.LgvApiBaseUrl}/clients/summary?${queryParam}&silenced=${ShowSilenced}&txtSearch=${searchText}`;
        if (filterOnClientsWithLoans) {
            bldUrl += `&hasLoanApplicationsOnly=true`;
        }
        return this.http.get<any>(bldUrl).pipe(first());
    }

    public SearchContacts(
        searchText: string,
        agentId: string
    ): Observable<any> {
        let queryParam = `searchText=${searchText}`;
        let bldUrl = `${env.LgvApiBaseUrl}/users/${agentId}/contacts?${queryParam}`;
        return this.http.get<any>(bldUrl).pipe(first());
    }

    public GetSortedActiveLoans(p: LgvModel.Property): LgvModel.Loan[] {
        let origNum = p.loans.length;

        let completeSortedLoans = p.loans
            .filter(f => this.IsCompleteLoan(f))
            .sort((a, b) => this.LoanCompareOrder(a, b));

        let rtnVal = completeSortedLoans.filter(f => this.LoanIsActive(f));

        let finalNum = rtnVal.length;
        if (finalNum < origNum && finalNum === 0) {
            console.log(
                '##### No Remaining Loans after removing incomplete or inactive loan(s) #####'
            );
        }
        return rtnVal;
    }

    public GetBestActiveLoan(p: LgvModel.Property) {
        let loans = this.GetSortedActiveLoans(p);
        return loans && loans.length > 0 ? loans[0] : null;
    }

    IsCompleteLoan(l: LgvModel.Loan): boolean {
        return l && l.loanAnalysis !== null && l.loanInfo !== null;
    }

    LoanIsActive(l: LgvModel.Loan): boolean {
        // treating isActive == null as active per Sheila on 6/8
        return (
            this.IsCompleteLoan(l) &&
            (l.loanInfo.isActive === null || l.loanInfo.isActive)
        );
    }

    public LoanCompareOrder(a: LgvModel.Loan, b: LgvModel.Loan): number {
        // Sort by IsActive => Lien Position => Loan Type, then closing date.
        // Sorrting rule basics:  compare returns <1 (less than 1) when "A" should preceed "B", and >1 when it should come after.
        // Stringing multiple comparisons with || allows priority on first, then defer to next comparison when they are "falsy" (equal comparison is 0 / "false")

        let result =
            (this.LoanIsActive(a) === this.LoanIsActive(b)
                ? 0
                : this.LoanIsActive(a)
                ? -1
                : 1) ||
            a.lienType.name.localeCompare(b.lienType.name) ||
            this.LoanTypeSortOrder(a) - this.LoanTypeSortOrder(b) ||
            (a.loanInfo.closingDate > b.loanInfo.closingDate ? -1 : 1);

        //console.log('LoanCompareOrder:');
        //console.log(this.LoanShortStr(a));
        //console.log(this.LoanShortStr(b));
        //console.log(result);

        return result;
    }

    public LoanDescription(l: LgvModel.Loan, p: LgvModel.Property): string {
        let lienPosition = '';

        switch (l.lienType.name) {
            case 'First Mortgage':
                lienPosition = '1st';
                break;
            case 'Second Mortgage':
                lienPosition = '2nd';
                break;
            default:
                lienPosition = 'Unknown Positon **';
                break;
        }

        let propertyStr = p && p.propertyInfo ? p.propertyInfo.address : '';

        return `${
            !l.loanAnalysis
                ? 'n/a'
                : '$' +
                  LgvTSHelper.numberWithCommas(
                      l.loanAnalysis.estimatedBalance.toFixed(0)
                  )
        } | ${l.loanInfo.interestRate.toFixed(3)}% | ${lienPosition} | ${
            l.loanType.name
        }`;
    }

    public LoanShortStr(l: LgvModel.Loan, p: LgvModel.Property): string {
        let lienPosition = '';

        if (!l.loanAnalysis) {
            return 'Missing Loan Analysis';
        }

        switch (l.lienType.name) {
            case 'First Mortgage':
                lienPosition = '1st';
                break;
            case 'Second Mortgage':
                lienPosition = '2nd';
                break;
            default:
                lienPosition = 'Unknown Positon **';
                break;
        }

        let propertyStr = p && p.propertyInfo ? p.propertyInfo.address : '[NA]';

        return `${l.loanInfo.alreadyRefinanced ? 1 : 0}${
            l.loanInfo.alreadyRefinancedAnotherLender ? 1 : 0
        }${l.loanInfo.alreadySold ? 1 : 0} | ${this.LoanIsActive(
            l
        )} | ${lienPosition} | ${
            l.loanAnalysis.loanType
        } | ${l.loanInfo.closingDate.toString().substr(0, 10)} | ${
            l.loanInfo.loanNumber
        } | ${propertyStr}`;
    }

    public LoanTypeSortOrder(l: LgvModel.Loan): number {
        if (!l.loanAnalysis) {
            console.log('Missing Loan Analysis');
            return 6;
        }

        switch (l.loanAnalysis.loanType) {
            case 'CONV':
                return 1;
            case 'FHA':
                return 2;
            case 'VA':
                return 3;
            case 'USDA/RHS':
                return 4;
            case 'HELOC':
                return 5;
            default:
                return 6;
        }
    }

    public SaveClient(param, id: string = '') {
        if (param.state && param.state.length > 0) {
            param.state = (param.state as string).toUpperCase();
        }

        let bldUrl = `${env.LgvApiBaseUrl}/clients`;
        if (id) {
            bldUrl = `${env.LgvApiBaseUrl}/clients/${id}`;
            return this.http.patch<any>(bldUrl, param);
        }
        return this.http.post<any>(bldUrl, param).pipe(first());
    }

    public addPublicClient(clientModel: any) {
        const bldUrl = `${env.LgvApiBaseUrl}/public/client`;
        return this.http.post<any>(bldUrl, clientModel).pipe(first());
    }

    public SaveLoanScenario(model: any) {
        let scenarioId: string = model.id;

        if (scenarioId) {
            let bldUrl = `${env.LgvApiBaseUrl}/clients/${model.clientId}/loanscenarios/${model.id}`;
            return this.http.patch<any>(bldUrl, model).pipe(first());
        } else {
            let bldUrl = `${env.LgvApiBaseUrl}/clients/${model.clientId}/loanscenarios`;
            return this.http.post<any>(bldUrl, model).pipe(first());
        }
    }

    public LoadClient(param) {
        let bldUrl = `${env.LgvApiBaseUrl}/clients/${param}`;
        return this.http.get<any>(bldUrl).pipe(first());
    }

    public FetchClientEquityGained(clientId: string, loanId: string) {
        if (
            this.Equity$.value != null &&
            this.Equity$.value.loanId == loanId &&
            this.Equity$.value.clientId == clientId
        ) {
            console.log('Already have equity details... Skipping fetch');
            return;
        }
        let bldUrl = `${env.LgvApiBaseUrl}/clients/${clientId}/loans/${loanId}/equity`;
        this.http
            .get<any>(bldUrl)
            .pipe(first(f => f !== null))
            .subscribe(
                res => {
                    this.Equity$.next(res);
                },
                err => {
                    console.log('Error in FetchClientEquityGained()');
                    console.log(err);
                }
            );
    }

    public PickBestLoanForProperty(property: LgvModel.Property): LgvModel.Loan {
        if (!property || !property.loans || property.loans.length == 0) {
            return null;
        }

        let activeLoans = property.loans.filter(f => this.LoanIsActive(f));
        if (activeLoans && activeLoans.length > 0) {
            let sortedLoans = activeLoans.sort((a, b) =>
                this.LoanCompareOrder(a, b)
            );

            if (sortedLoans && sortedLoans.length > 0) {
                return sortedLoans[0];
            }
        }
        return null;
    }

    public updateClientProperty(
        clientId: string,
        propertyId: string,
        model: any
    ): Observable<any> {
        // use this endpoint when updating the clientProperty and Property.  ***ListingPrice is stored in the ClientProperty table***
        let bldUrl = `${env.LgvApiBaseUrl}/clients/${clientId}/properties/${propertyId}`;
        return this.http.patch<any>(bldUrl, model);
    }

    public ActiveLoansForProperty(p: LgvModel.Property): LgvModel.Loan[] {
        if (!p || !p.loans || p.loans.length === 0) {
            return [];
        }

        let sortedLoans = p.loans.sort((a, b) => this.LoanCompareOrder(a, b));
        return sortedLoans.filter(f => this.LoanIsActive(f));
    }

    public InactiveLoansForProperty(p: LgvModel.Property): LgvModel.Loan[] {
        //console.log('InactiveLoansForProperty()');
        if (!p || !p.loans || p.loans.length === 0) {
            return [];
        }

        let sortedLoans = p.loans.sort((a, b) => this.LoanCompareOrder(a, b));
        let rtnVal = sortedLoans.filter(f => !this.LoanIsActive(f));

        //console.log('rtnVal:');
        //console.log(rtnVal);

        return rtnVal;
    }

    public ApplyNow(param: any) {
        let bldUrl = `${env.LgvApiBaseUrl}/properties/${param.property.id}/applications?clientId=${param.client.id}`;

        // Track loan application start
        this.analytics.trackLoanApplication('start', {
            property_id: param.property.id,
            client_id: param.client.id
        });

        this.http
            .post<BlendModel.BlendResponse>(bldUrl, param)
            .pipe(first())
            .subscribe(
                res => {
                    res.error = null;
                    this.BlendData$.next(res);

                    // Track loan application complete if successful
                    if (!res.error && res.loan?.redirectUrl) {
                        this.analytics.trackLoanApplication('complete', {
                            property_id: param.property.id,
                            client_id: param.client.id,
                            redirect_url: res.loan.redirectUrl
                        });
                    }
                },
                err => {
                    console.log('Error posting Blend Application()');
                    console.log(err);

                    this.BlendData$.next({
                        error: 'Error posting to blend...'
                    });
                }
            );
    }

    public deleteProperty(clientId: string, propertyId: string) {
        this.isPropertyDeletedLoading$.next(true);
        const bldUrl = `${env.LgvApiBaseUrl}/clients/${clientId}/properties/${propertyId}`;
        this.http
            .delete(bldUrl)
            .pipe(
                first(),
                tap(() => {
                    if (this.authMgr.IsClient) {
                        // If user is both client and secureClient, two auth events will fire.  Only load snapshot for IsClient
                        if (!this.authMgr.IsSecureClient) {
                            this.LoadSnapshotForClient();
                        }
                    } else if (this.SavedImpersonatedClientID) {
                        this.SnapShot$.next(undefined);
                        this.LoadSnapshotForNonClient(
                            this.SavedImpersonatedClientID
                        );
                    }
                    this.isPropertyDeletedLoading$.next(false);
                })
            )
            .subscribe(() => {
                error: err => {
                    console.log('Error deleting property');
                    console.log(err);
                    this.isPropertyDeletedLoading$.next(false);
                };
            });
    }

    public adjustHomeValue(
        clientId: string,
        propertyId: string,
        model: any,
        isNewAdjustment: boolean,
        adjustmentId?: string
    ) {
        if (isNewAdjustment) {
            const url = `${env.LgvApiBaseUrl}/clients/${clientId}/properties/${propertyId}/adjustments`;
            return this.http.post<any>(url, model).pipe(first());
        } else {
            const url = `${env.LgvApiBaseUrl}/clients/${clientId}/properties/${propertyId}/adjustments/${adjustmentId}`;
            return this.http.patch<any>(url, model).pipe(first());
        }
    }

    resetHomeValue(
        clientId: string,
        propertyId: string,
        adjustmentId?: string
    ) {
        const url = `${env.LgvApiBaseUrl}/clients/${clientId}/properties/${propertyId}/adjustments/${adjustmentId}`;
        return this.http.delete<any>(url).pipe(first());
    }

    public generateClientToken(
        clientId: string,
        months: number
    ): Observable<any> {
        let today = new Date();
        let expirationDate = new Date(
            today.setMonth(today.getMonth() + months)
        );

        let model = {
            id: clientId,
            expiration: LgvTSHelper.jsDateToJsonDate(expirationDate, true)
        };

        let bldUrl = `${env.LgvApiBaseUrl}/auth/client/request/token`;
        return this.http.post<any>(bldUrl, model);
    }

    public getClientOpportunities(
        clientId: string,
        loanId?: string,
        loanScenarioId?: string,
        pricingScenarioID?: string,
        forceRefresh: boolean = false
    ) {
        let bldUrl = `${env.LgvApiBaseUrl}/clients/${clientId}/opportunities?force_refresh=${forceRefresh}`;

        if (!!loanId) {
            bldUrl += `&loanId=${loanId}`;
        }
        if (!!loanScenarioId) {
            bldUrl += `&loanScenarioId=${loanScenarioId}`;
        }
        if (!!pricingScenarioID) {
            bldUrl += `&pricingScenarioId=${pricingScenarioID}`;
        }
        // API needs this to get all opportunities for a client
        if (!(!!loanId || !!loanScenarioId || !!pricingScenarioID)) {
            bldUrl += `&getAllPricingScenarios=true`;
        }

        return this.http.get<LgvModel.Opportunity[]>(bldUrl).pipe(
            first(),
            catchError(err => {
                console.log(err);
                return of(null);
            }),
            map(x => {
                return x?.sort((a, b) =>
                    a.opportunityInfo?.paymentSavings >
                    b.opportunityInfo?.paymentSavings
                        ? -1
                        : 1
                );
            })
        );
    }

    public getClientDisclosures(
        clientId: string
    ): Observable<LgvModel.ClientDisclosures> {
        let bldUrl = `${env.LgvApiBaseUrl}/clients/${clientId}/disclosures`;
        return this.http.get<LgvModel.ClientDisclosures>(bldUrl);
    }

    public deleteClientLoan(clientId: string, loanId: string): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/clients/${clientId}/loans/${loanId}`;
        return this.http.delete<any>(bldUrl).pipe(
            tap(() => {
                if (this.authMgr.IsClient) {
                    // If user is both client and secureClient, two auth events will fire.  Only load snapshot for IsClient
                    if (!this.authMgr.IsSecureClient) {
                        this.LoadSnapshotForClient();
                    }
                } else if (this.SavedImpersonatedClientID) {
                    this.LoadSnapshotForNonClient(
                        this.SavedImpersonatedClientID,
                        true
                    );
                }
            })
        );
    }

    public fetchClientProperties(clientId) {
        let bldUrl = `${env.LgvApiBaseUrl}/clients/${clientId}/properties`;
        return this.http.get<any[]>(bldUrl);
    }
}
